import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { AlertTriangle, TrendingDown, TrendingUp } from 'lucide-react'

export interface SupplierRanking {
  supplier_id: number
  supplier_name: string
  value: number
  target?: number
  trend?: 'up' | 'down' | 'stable' // vs last month
  percentage_change?: number
}

interface SupplierRankingCardProps {
  title: string
  suppliers: SupplierRanking[]
  metricType: 'ppm' | 'otif' | 'otd' | 'arrears' | 'cost'
  formatValue: (value: number) => string
  inverse?: boolean // true if lower is better (e.g., PPM, arrears)
  onSupplierClick?: (supplierId: number) => void
}

export function SupplierRankingCard({
  title,
  suppliers,
  metricType: _metricType,
  formatValue,
  inverse = false,
  onSupplierClick
}: SupplierRankingCardProps) {

  // Determine color based on performance
  const getStatusColor = (supplier: SupplierRanking) => {
    if (!supplier.target) return 'text-gray-600'

    const isGood = inverse
      ? supplier.value <= supplier.target
      : supplier.value >= supplier.target

    return isGood ? 'text-green-600' : 'text-red-600'
  }

  const getTrendIcon = (trend?: 'up' | 'down' | 'stable') => {
    if (!trend) return null

    // For inverse metrics (PPM, arrears), up is bad, down is good
    // For normal metrics (OTIF, OTD), up is good, down is bad
    const isGoodTrend = inverse
      ? trend === 'down'
      : trend === 'up'

    if (trend === 'up') {
      return <TrendingUp className={`h-4 w-4 ${isGoodTrend ? 'text-green-500' : 'text-red-500'}`} />
    } else if (trend === 'down') {
      return <TrendingDown className={`h-4 w-4 ${isGoodTrend ? 'text-green-500' : 'text-red-500'}`} />
    }
    return null
  }

  if (suppliers.length === 0) {
    return (
      <Card className="shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-yellow-500" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-4">
            No data available
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="shadow-sm hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-red-500" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-3">
          {suppliers.map((supplier, index) => (
            <div
              key={supplier.supplier_id}
              className={`flex items-center justify-between p-2 rounded-md hover:bg-muted transition-colors ${
                onSupplierClick ? 'cursor-pointer' : ''
              }`}
              onClick={() => onSupplierClick?.(supplier.supplier_id)}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {/* Rank */}
                <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                  index === 0 ? 'bg-red-500 text-white' :
                  index === 1 ? 'bg-orange-500 text-white' :
                  index === 2 ? 'bg-yellow-500 text-white' :
                  'bg-gray-300 text-gray-700'
                }`}>
                  {index + 1}
                </div>

                {/* Supplier Name */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {supplier.supplier_name}
                  </p>
                  {supplier.percentage_change !== undefined && (
                    <p className={`text-xs ${
                      inverse
                        ? supplier.percentage_change > 0 ? 'text-red-500' : 'text-green-500'
                        : supplier.percentage_change > 0 ? 'text-green-500' : 'text-red-500'
                    }`}>
                      {supplier.percentage_change > 0 ? '+' : ''}
                      {supplier.percentage_change.toFixed(1)}% vs last month
                    </p>
                  )}
                </div>
              </div>

              {/* Value and Trend */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {getTrendIcon(supplier.trend)}
                <div className="text-right">
                  <p className={`text-sm font-bold ${getStatusColor(supplier)}`}>
                    {formatValue(supplier.value)}
                  </p>
                  {supplier.target !== undefined && (
                    <p className="text-xs text-muted-foreground">
                      Target: {formatValue(supplier.target)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Summary Footer */}
        <div className="mt-4 pt-3 border-t">
          <p className="text-xs text-muted-foreground text-center">
            Click supplier for detailed breakdown
          </p>
        </div>
      </CardContent>
    </Card>
  )
}
